import type { ReactNode } from 'react';
import type {
  LeadTimeLine,
  LedgerEventView,
  MonthBucket,
  PlanRow,
  SellThroughDetail,
  SellThroughMonth,
  SellThroughWindow,
} from '../api/client.js';
import { CONFIDENCE_CLASS, CONFIDENCE_LABEL, VELOCITY_SOURCE_LABEL } from '../lib/purchase-plan.js';
import { Card, TableScroll } from './ui.js';
import { day, dateTime, decimal, integer, money, monthLabel } from '../format.js';

/**
 * The detail sections the SKU page and the product page share: the 24-month
 * table, the ledger events, sell-through and stockouts, the lead-time lines, and
 * one plan row's figures. Presentational only — each page fetches its own data —
 * so the two pages read the same numbers the same way.
 */

/** How many ledger events the API returns (analysis/sku-history.ts LEDGER_EVENTS_SHOWN). */
export const LEDGER_EVENTS_SHOWN = 200;

export function Item({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="text-body">{value}</dd>
    </div>
  );
}

export function Num({ value }: { value: number }) {
  return <td className="tabular px-3 py-1.5 text-right">{integer(value)}</td>;
}

/**
 * The last 24 months, newest first. `monthMeta` adds one SKU's month-end on hand,
 * days in stock, and trailing-90 sell-through from the derived tables; a product
 * (several SKUs) leaves it out, since those are per-SKU figures.
 */
export function MonthlyHistoryCard({
  months,
  monthMeta,
  title = 'Last 24 months',
}: {
  months: MonthBucket[];
  monthMeta?: Map<string, SellThroughMonth>;
  title?: string;
}) {
  return (
    <Card>
      <div className="border-b border-line px-4 py-2">
        <h2 className="text-sm font-medium text-body">{title}</h2>
      </div>
      <TableScroll>
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-xs text-muted">
            <tr>
              <th className="px-3 py-2 font-medium">Month</th>
              <th className="px-3 py-2 text-right font-medium">Units</th>
              <th className="px-3 py-2 text-right font-medium">Orders</th>
              <th className="px-3 py-2 text-right font-medium">Revenue</th>
              <th className="px-3 py-2 text-right font-medium">Sessions</th>
              <th className="px-3 py-2 text-right font-medium">Traffic units</th>
              <th className="px-3 py-2 text-right font-medium">Receipts</th>
              <th className="px-3 py-2 text-right font-medium">Shipments</th>
              <th className="px-3 py-2 text-right font-medium">Returns</th>
              <th className="px-3 py-2 text-right font-medium">Adjustments</th>
              <th className="px-3 py-2 text-right font-medium">Transfers</th>
              {monthMeta && (
                <>
                  <th className="px-3 py-2 text-right font-medium" title="On hand at month end">
                    On hand
                  </th>
                  <th
                    className="px-3 py-2 text-right font-medium"
                    title="Days in stock in the month"
                  >
                    Days IS
                  </th>
                  <th
                    className="px-3 py-2 text-right font-medium"
                    title="Trailing-90 sell-through at month end"
                  >
                    ST 90
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {[...months].reverse().map((m) => {
              const st = monthMeta?.get(m.month);
              return (
                <tr key={m.month} className={m.units === 0 && m.sessions === 0 ? 'text-muted' : ''}>
                  <td className="whitespace-nowrap px-3 py-1.5">{monthLabel(m.month)}</td>
                  <Num value={m.units} />
                  <Num value={m.orders} />
                  <td className="tabular px-3 py-1.5 text-right">{money(m.revenue)}</td>
                  <Num value={m.sessions} />
                  <Num value={m.trafficUnits} />
                  <Num value={m.receipts} />
                  <Num value={m.shipments} />
                  <Num value={m.customerReturns} />
                  <Num value={m.adjustments} />
                  <Num value={m.whseTransfers} />
                  {monthMeta && (
                    <>
                      <td className="tabular px-3 py-1.5 text-right">
                        {st ? integer(st.monthEndOnHand) : '—'}
                      </td>
                      <td className="tabular px-3 py-1.5 text-right">
                        {st ? integer(st.daysInStock) : '—'}
                      </td>
                      <td className="tabular px-3 py-1.5 text-right">
                        {st ? decimal(st.sellThrough90, 2) : '—'}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableScroll>
    </Card>
  );
}

/** The latest ledger events, newest first; `showSku` adds the seller SKU column. */
export function LedgerCard({
  ledger,
  shown,
  showSku = false,
}: {
  ledger: LedgerEventView[];
  /** The cap the server applied; the heading says "latest N" when it was reached. */
  shown: number;
  showSku?: boolean;
}) {
  const columns = showSku ? 8 : 7;
  return (
    <Card>
      <div className="border-b border-line px-4 py-2">
        <h2 className="text-sm font-medium text-body">
          Ledger events{ledger.length === shown ? ` (latest ${shown})` : ''}
        </h2>
      </div>
      <TableScroll>
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-xs text-muted">
            <tr>
              <th className="px-3 py-2 font-medium">Date</th>
              {showSku && <th className="px-3 py-2 font-medium">SKU</th>}
              <th className="px-3 py-2 font-medium">Event</th>
              <th className="px-3 py-2 font-medium">Reference</th>
              <th className="px-3 py-2 text-right font-medium">Qty</th>
              <th className="px-3 py-2 font-medium">FC</th>
              <th className="px-3 py-2 font-medium">Disposition</th>
              <th className="px-3 py-2 font-medium">Reason</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {ledger.map((e) => (
              <tr key={e.id}>
                <td className="whitespace-nowrap px-3 py-1.5">
                  {e.eventTime ? dateTime(e.eventTime) : day(e.eventDate)}
                </td>
                {showSku && (
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs">
                    {e.msku ?? '—'}
                  </td>
                )}
                <td className="px-3 py-1.5 text-body">{e.eventType ?? '—'}</td>
                <td className="px-3 py-1.5 text-muted">{e.referenceId ?? '—'}</td>
                <Num value={e.quantity} />
                <td className="px-3 py-1.5 text-muted">{e.fulfillmentCenter ?? '—'}</td>
                <td className="px-3 py-1.5 text-muted">{e.disposition ?? '—'}</td>
                <td className="px-3 py-1.5 text-muted">{e.reason ?? '—'}</td>
              </tr>
            ))}
            {ledger.length === 0 && (
              <tr>
                <td colSpan={columns} className="px-3 py-6 text-center text-sm text-muted">
                  No ledger events. Import an Inventory Ledger detailed view.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </TableScroll>
    </Card>
  );
}

/** The two sell-through windows side by side, with the anchor and quality notes. */
export function SellThroughCard({
  detail,
  title = 'Sell-through',
}: {
  detail: SellThroughDetail;
  title?: string;
}) {
  return (
    <Card className="p-5">
      <h2 className="text-sm font-medium text-body">{title}</h2>
      {!detail.hasData ? (
        <p className="mt-2 text-sm text-muted">
          No derived daily data yet. Import an Inventory Ledger and an All Orders file, then
          rebuild.
        </p>
      ) : (
        <>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <WindowPanel title="Current (last 90 days loaded)" window={detail.current} />
            <WindowPanel
              title="Last stocked (90 days to last in stock)"
              window={detail.lastStocked}
            />
          </div>
          <p className="mt-3 border-t border-line pt-2 text-xs text-muted">{qualityNote(detail)}</p>
        </>
      )}
    </Card>
  );
}

function WindowPanel({ title, window }: { title: string; window: SellThroughWindow }) {
  return (
    <div className="rounded-md bg-sunken p-3">
      <h3 className="text-xs font-medium text-body">{title}</h3>
      {window.endDay === null ? (
        <p className="mt-2 text-xs text-muted">Never in stock.</p>
      ) : (
        <>
          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <Item label="Sell-through" value={decimal(window.sellThrough, 2)} />
            <Item label="Units per 30 in stock" value={decimal(window.unitsPer30InStock, 2)} />
            <Item label="Units shipped" value={integer(window.unitsShipped)} />
            <Item label="Avg on hand" value={decimal(window.avgOnHand, 2)} />
            <Item label="Days in stock" value={integer(window.daysInStock)} />
            <Item label="Known days" value={integer(window.windowDays)} />
            <Item label="Window ends" value={day(window.endDay)} />
          </dl>
          {window.shortHistory && (
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
              Fewer than 30 known days — rests on little data.
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** The anchoring and data-quality facts, in plain words. */
function qualityNote(detail: SellThroughDetail): string {
  const parts: string[] = [];
  if (detail.knownFrom && detail.knownTo) {
    parts.push(
      `On-hand is known from ${day(detail.knownFrom)} through ${day(detail.knownTo)} (the loaded ledger).`,
    );
  }
  if (detail.anchor === 'forward') {
    parts.push(
      'No census lies inside ledger coverage, so on-hand is a forward estimate (a lower bound) — sell-through and stockouts are left out until a ledger covering the latest census is uploaded.',
    );
  } else {
    parts.push(
      `Anchored to the FBA inventory census inside that range${detail.anchorReserved ? ', on fulfillable + reserved units' : ''}.`,
    );
  }
  if (!detail.hasLedger) parts.push('This SKU has no ledger data, so on-hand is unknown.');
  else if (detail.anchor === 'census' && !detail.hasSellable)
    parts.push(
      'The ledger never shows a sellable balance, so the score uses the active-month average.',
    );
  if (detail.negativeDays > 0) {
    parts.push(
      `On-hand went negative on ${detail.negativeDays} day${detail.negativeDays === 1 ? '' : 's'} — the ledger is missing coverage there, so those days are left unknown.`,
    );
  }
  if (detail.ledgerGaps)
    parts.push('The ledger has a 7+ day gap over this range; on-hand is carried across it.');
  if (detail.salesBeforeLedger)
    parts.push('This SKU sold before its ledger begins; that early history is unknown.');
  return parts.join(' ');
}

/** One SKU's stockout episodes, oldest first. */
export function StockoutsCard({
  detail,
  title = 'Stockouts',
}: {
  detail: SellThroughDetail;
  title?: string;
}) {
  if (!detail.hasData) return null;
  return (
    <Card>
      <div className="border-b border-line px-4 py-2">
        <h2 className="text-sm font-medium text-body">
          {title}
          {detail.stockouts.length === 20 ? ' (latest 20)' : ''}
        </h2>
      </div>
      <TableScroll>
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-xs text-muted">
            <tr>
              <th className="px-3 py-2 font-medium">Start</th>
              <th className="px-3 py-2 font-medium">End</th>
              <th className="px-3 py-2 text-right font-medium">Days</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {detail.stockouts.map((s) => (
              <tr key={s.start}>
                <td className="whitespace-nowrap px-3 py-1.5">{day(s.start)}</td>
                <td className="whitespace-nowrap px-3 py-1.5">
                  {s.ongoing ? (
                    <span className="text-amber-600 dark:text-amber-400">Ongoing</span>
                  ) : (
                    day(s.end)
                  )}
                </td>
                <td className="tabular px-3 py-1.5 text-right">{integer(s.days)}</td>
              </tr>
            ))}
            {detail.stockouts.length === 0 && (
              <tr>
                <td colSpan={3} className="px-3 py-6 text-center text-sm text-muted">
                  No stockouts after being in stock.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </TableScroll>
    </Card>
  );
}

/** A plain-words label for a lead-time line's match status. */
export const LEAD_STATUS_LABEL: Record<LeadTimeLine['status'], string> = {
  matched: 'Received',
  in_transit: 'In transit',
  awaiting: 'Awaiting shipment',
  unmatched: 'No shipment',
};

/** Whether any two matched lines have shipments created within 14 days of each other. */
export function hasAdjacentShipments(lines: LeadTimeLine[]): boolean {
  const created = lines
    .map((l) => (l.shipmentCreatedAt ? Date.parse(l.shipmentCreatedAt) : NaN))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);
  const FOURTEEN_DAYS = 14 * 24 * 60 * 60_000;
  for (let i = 1; i < created.length; i += 1) {
    if ((created[i] as number) - (created[i - 1] as number) <= FOURTEEN_DAYS) return true;
  }
  return false;
}

/** The note shown above adjacent orders, whose pairing is by sequence. */
export function AdjacentShipmentsNote() {
  return (
    <p className="border-b border-line bg-sunken px-4 py-2 text-xs text-muted">
      Adjacent orders (shipments within 14 days); pairing is by sequence — the oldest order takes
      the oldest shipment. Check the shipment ids if a pairing looks off. See docs/lead-times.md.
    </p>
  );
}

/**
 * The observed lead-time lines: every sent plan line with the shipment it became,
 * the dates, and the supplier / transit / total days. `showSku` adds the SKU column.
 */
export function LeadTimeLinesTable({
  lines,
  showSku = false,
}: {
  lines: LeadTimeLine[];
  showSku?: boolean;
}) {
  return (
    <TableScroll>
      <table className="w-full text-sm">
        <thead className="border-b border-line text-left text-xs text-muted">
          <tr>
            <th className="px-3 py-2 font-medium">Plan</th>
            {showSku && <th className="px-3 py-2 font-medium">SKU</th>}
            <th className="px-3 py-2 font-medium">Sent</th>
            <th className="px-3 py-2 font-medium">Shipment</th>
            <th className="px-3 py-2 font-medium">Created</th>
            <th className="px-3 py-2 font-medium">Received</th>
            <th className="px-3 py-2 text-right font-medium" title="Order sent to shipment created">
              Supplier
            </th>
            <th className="px-3 py-2 text-right font-medium" title="Shipment created to check-in">
              Transit
            </th>
            <th className="px-3 py-2 text-right font-medium" title="Order sent to check-in">
              Total
            </th>
            <th className="px-3 py-2 font-medium">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line" data-testid="lead-time-lines">
          {lines.map((line, i) => (
            <tr key={`${line.planId}-${line.sellerSku}-${line.shipmentId ?? 'none'}-${i}`}>
              <td className="px-3 py-1.5">{line.planName}</td>
              {showSku && (
                <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs">
                  {line.sellerSku}
                </td>
              )}
              <td className="whitespace-nowrap px-3 py-1.5">{day(line.sentAt)}</td>
              <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs">
                {line.shipmentId ?? '—'}
                {line.otherShipments.length > 0 && (
                  <div className="mt-0.5 text-[11px] text-muted">
                    + also{' '}
                    {line.otherShipments.map((s) => s.confirmationId ?? s.shipmentId).join(', ')}
                  </div>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-1.5">{day(line.shipmentCreatedAt)}</td>
              <td className="whitespace-nowrap px-3 py-1.5">{day(line.firstReceiptAt)}</td>
              <td className="tabular px-3 py-1.5 text-right">
                {line.supplierDays === null ? '—' : `${integer(line.supplierDays)}d`}
              </td>
              <td className="tabular px-3 py-1.5 text-right">
                {line.transitDays === null ? '—' : `${integer(line.transitDays)}d`}
              </td>
              <td className="tabular px-3 py-1.5 text-right font-medium">
                {line.totalDays === null ? '—' : `${integer(line.totalDays)}d`}
              </td>
              <td className="whitespace-nowrap px-3 py-1.5 text-muted">
                {LEAD_STATUS_LABEL[line.status]}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

/** One plan row's figures: what to buy and why. Editing is on the plan page. */
export function PlanRowFacts({ row }: { row: PlanRow }) {
  return (
    <>
      <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
        <Item label="Suggested qty" value={integer(row.suggestedQty)} />
        <Item label="Final qty" value={integer(row.finalQty)} />
        <Item label="On hand + inbound" value={integer(row.position)} />
        <Item
          label="Velocity / 30"
          value={`${decimal(row.velocityPer30, 1)} (${VELOCITY_SOURCE_LABEL[row.velocitySource]})`}
        />
        <Item label="Demand" value={decimal(row.demand, 0)} />
        <Item label="Days of cover with inbound" value={decimal(row.daysOfCoverNow, 0)} />
        <Item label="Stockout" value={day(row.projectedStockoutDate)} />
        <div>
          <dt className="text-xs text-muted">Order by</dt>
          <dd className={row.late ? 'font-medium text-danger-text' : 'text-body'}>
            {day(row.orderByDate)}
          </dd>
        </div>
        <Item label="Unit cost" value={money(row.unitCost)} />
        <Item label="Extended cost" value={money(row.extendedCost)} />
        <div>
          <dt className="text-xs text-muted">Confidence</dt>
          <dd>
            <span
              className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ${CONFIDENCE_CLASS[row.confidence]}`}
            >
              {CONFIDENCE_LABEL[row.confidence]}
            </span>
          </dd>
        </div>
      </dl>
      {row.reasons.length > 0 && (
        <ul className="mt-3 list-disc border-t border-line pt-2 pl-5 text-xs text-muted">
          {row.reasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      )}
    </>
  );
}
